import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
	Modal,
	ModalBody,
	ModalHeader,
} from 'reactstrap';

import IconButton from '../IconButton';

import './LearnMoreModal.scss';

const EVENT_KEYCODE_SPACEBAR = ' ';
const EVENT_KEYCODE_ENTER = 'Enter';

const propTypes = {

	/**
	 * The ID for the modal button element.
	 */
	id: PropTypes.string.isRequired,

	/**
	 * The hover title text for the modal button element,
	 * used to describe the contents of the modal itself.
	 */
	title: PropTypes.string.isRequired,

	/**
	 * The size of the modal button element (including the hover background).
	 */
	size: PropTypes.string,

	/**
	 * The modal header content.
	 */
	header: PropTypes.oneOfType([
		PropTypes.element,
		PropTypes.string,
		PropTypes.func,
	]).isRequired,

	/**
	 * The modal body content.
	 */
	body: PropTypes.oneOfType([
		PropTypes.element,
		PropTypes.string,
		PropTypes.func,
	]).isRequired,

};

/**
 * Shared component to render a TechChange "Learn More" modal,
 * which can be used for inline documentation and platform education.
 *
 * @param {Object} props The props.
 *
 * @returns {Object} The rendered component.
 */
const LearnMoreModal = (props) => {

	const {
		id,
		title,
		size,
		body,
		header,
		...restProps
	} = props;

	const [isOpen, setIsOpen] = useState(false);

	const modalContentId = `${id}-content`;
	const modalContainerId = `${id}-container`;

	return (
		<div
			{...restProps}
			id={modalContainerId}
			className={`
				tc-learn-more-modal-container
				${restProps.className || ''}
			`}>
			<IconButton
				id={id}
				icon="fa-light fa-question-circle"
				size={size || '24px'}
				title={title}
				aria-expanded={isOpen}
				aria-controls={modalContentId}
				tooltip
				// Handle toggling open via click
				onClick={(event) => {

					// NOTE: Manually stop propagation of the click event
					//       in case this button is the child of a parent element
					//       that also needs to handle click events
					event.stopPropagation();

					setIsOpen(true);

				}}
				// Handle toggling open via space/enter keys
				onKeyDown={(event) => {

					if (event.key === EVENT_KEYCODE_SPACEBAR ||
						event.key === EVENT_KEYCODE_ENTER) {

						// NOTE: Manually stop propagation of the click event
						//       in case this button is the child of a parent element
						//       that also needs to handle click events
						event.stopPropagation();

						setIsOpen(true);

					}

				}} />
			<Modal
				id={modalContentId}
				className="tc-learn-more-modal"
				size="lg"
				isOpen={isOpen}
				container={modalContainerId}
				toggle={() => setIsOpen((prev) => !prev)}>
				<ModalHeader
					tag="h1"
					className="py-2"
					toggle={() => setIsOpen((prev) => !prev)}>
					{header}
				</ModalHeader>
				<ModalBody>
					{body}
				</ModalBody>
			</Modal>
		</div>
	);

};

LearnMoreModal.propTypes = propTypes;

export default LearnMoreModal;
